import { useEffect, useRef } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Warning } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Modal } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import type { INoTouchSuggestion } from "app/components/no-touch-purchase-modal/suggestions";
import { NoTouchPurchaseSummaryModalDetails } from "app/components/no-touch-purchase-modal/summary-modal/details/NoTouchPurchaseSummaryModalDetails";
import { SelectorLoader } from "app/components/no-touch-purchase-modal/summary-modal/loaders/SelectorLoader";
import { NoTouchPurchaseSummaryModalSelection } from "app/components/no-touch-purchase-modal/summary-modal/selection/NoTouchPurchaseSummaryModalSelection";
import {
    NoTouchPurchaseSummaryModalContent,
    NoTouchPurchaseSummaryModalDetailsContainer,
    NoTouchPurchaseSummaryModalError,
} from "app/components/no-touch-purchase-modal/summary-modal/StyledNoTouchPurchaseSummaryModal";
import { NoTouchPurchaseSummaryModalSuggestions } from "app/components/no-touch-purchase-modal/summary-modal/suggestions/NoTouchPurchaseSummaryModalSuggestions";
import { NoTouchPurchaseSummaryModalTitle } from "app/components/no-touch-purchase-modal/summary-modal/title/NoTouchPurchaseSummaryModalTitle";
import {
    useNoTouchPurchaseSummaryModal,
    type INoTouchAddon,
} from "app/components/no-touch-purchase-modal/summary-modal/useNoTouchPurchaseSummaryModal";
import { NO_TOUCH_PURCHASE_TRANSLATIONS } from "app/components/no-touch-purchase-modal/translations";
import { ADDON_TYPE_NAME_DICT, type AddonTypeName } from "app/contexts/AddonStateContext/const";

const purchaseModalStyle: ReactModal.Styles = {
    content: {
        width: "800px",
        minHeight: "561px",
        border: 0,
    },
};

interface INoTouchPurchaseSummaryModalProps {
    isOpen: boolean;
    isPurchaseFailed: boolean;
    addonType: AddonTypeName;
    preselectQuantity?: number;
    preselectVariation?: string;
    selectedAddon?: INoTouchAddon;
    suggestions: INoTouchSuggestion[];
    onClose: () => void;
    onPurchaseClick: () => void;
    onSelectedAddonChange: (newAddon: INoTouchAddon) => void;
}

interface IGetSelectedAddonProps {
    addonType: AddonTypeName;
    selection: INoTouchAddon[];
    preselectQuantity?: number;
    preselectVariation?: string;
}

const getSelectedAddon = ({
    addonType,
    selection,
    preselectQuantity,
    preselectVariation,
}: IGetSelectedAddonProps): INoTouchAddon | undefined => {
    switch (addonType) {
        case ADDON_TYPE_NAME_DICT.CountryFilterDynamic:
            return selection.find((item) => item.title === `CountryFilter${preselectVariation}`);
        default:
            return selection.find((item) => item.quantity === preselectQuantity);
    }
};

const getNextUpgradeAddon = (selection: INoTouchAddon[]): INoTouchAddon => {
    const purchasedIndex = selection.findIndex((item) => item.isPurchased);

    // nothing owned yet -> start at the lowest tier
    if (purchasedIndex === -1) {
        return selection[0];
    }

    // owns a tier -> preselect the next tier up (fall back to the highest if already at max)
    return selection[purchasedIndex + 1] ?? selection[selection.length - 1];
};

export const NoTouchPurchaseSummaryModal = ({
    isOpen,
    isPurchaseFailed,
    addonType,
    preselectQuantity,
    preselectVariation,
    selectedAddon,
    suggestions,
    onClose,
    onPurchaseClick,
    onSelectedAddonChange,
}: INoTouchPurchaseSummaryModalProps) => {
    const { translate } = useTranslate();
    const preselectionUsed = useRef<boolean>(false);

    const { isLoading, parsedData: data } = useNoTouchPurchaseSummaryModal({ addonType, translate });

    useEffect(() => {
        preselectionUsed.current = false;
    }, [preselectVariation, addonType]);

    useEffect(() => {
        if (!data?.selection.length || preselectionUsed.current) {
            return;
        }

        const nextAddon =
            getSelectedAddon({
                addonType,
                selection: data.selection,
                preselectQuantity,
                preselectVariation,
            }) ?? getNextUpgradeAddon(data.selection);

        if (selectedAddon?.id === nextAddon.id && selectedAddon.productTemplateId === nextAddon.productTemplateId) {
            return;
        }

        preselectionUsed.current = true;
        onSelectedAddonChange(nextAddon);
    }, [
        data,
        addonType,
        preselectQuantity,
        preselectVariation,
        onSelectedAddonChange,
        selectedAddon?.id,
        selectedAddon?.productTemplateId,
    ]);

    return (
        <Modal
            isOpen={isOpen}
            customStyles={purchaseModalStyle}
            shouldCloseOnEsc={!isLoading}
            shouldCloseOnOverlayClick={!isLoading}
            onRequestClose={onClose}
        >
            <NoTouchPurchaseSummaryModalTitle
                addonType={addonType}
                onClose={onClose}
            />

            <NoTouchPurchaseSummaryModalContent $isLoading={isLoading}>
                <>
                    {isPurchaseFailed ? (
                        <NoTouchPurchaseSummaryModalError>
                            <Warning
                                color={colorsPalettes.red[500]}
                                size="sm"
                            />
                            <Text
                                fontVariant="body1"
                                color={colorsPalettes.red[500]}
                            >
                                {translate(NO_TOUCH_PURCHASE_TRANSLATIONS.details.errorText)}
                            </Text>
                        </NoTouchPurchaseSummaryModalError>
                    ) : null}
                    <Flex
                        width="800px"
                        height="100%"
                    >
                        <Flex
                            width="480px"
                            flexDirection="column"
                            padding="24px"
                            gap="24px"
                        >
                            {isLoading || !selectedAddon ? (
                                <SelectorLoader />
                            ) : (
                                <>
                                    <NoTouchPurchaseSummaryModalSelection
                                        currency={data?.currency}
                                        addonType={addonType}
                                        selection={data?.selection}
                                        selectedAddon={selectedAddon}
                                        onSelectedAddonChange={onSelectedAddonChange}
                                    />

                                    <NoTouchPurchaseSummaryModalSuggestions
                                        addonType={addonType}
                                        suggestions={suggestions}
                                    />
                                </>
                            )}
                        </Flex>

                        <NoTouchPurchaseSummaryModalDetailsContainer>
                            <NoTouchPurchaseSummaryModalDetails
                                addonType={addonType}
                                selectIsLoading={isLoading}
                                currency={data?.currency}
                                selectedAddon={selectedAddon}
                                onPurchaseClick={onPurchaseClick}
                            />
                        </NoTouchPurchaseSummaryModalDetailsContainer>
                    </Flex>
                </>
            </NoTouchPurchaseSummaryModalContent>
        </Modal>
    );
};
